// services/activityService.js — SharePoint CRUD for SCMActivities list
import { getSiteId, fetchAllPages, GRAPH_PAGE_SIZE } from "./sharepointService";

const graphBase = "https://graph.microsoft.com/v1.0";
const ACTIVITIES_LIST = "SCMActivities";

function spToActivity(fields) {
  let updates = [];
  try { updates = JSON.parse(fields.UpdatesData || "[]"); } catch {}

  // Parse pipe-separated owner lists (backward compat: single owner has no pipe)
  const emails = (fields.OwnerEmail || "").split("|").filter(Boolean);
  const names  = (fields.OwnerName  || "").split("|").filter(Boolean);
  const owners = emails.map((email, i) => ({
    email,
    name:       names[i] || email,
    jobTitle:   i === 0 ? (fields.OwnerJobTitle   || "") : "",
    department: i === 0 ? (fields.OwnerDepartment || "") : "",
  }));

  let cfoRecommendations = [];
  try { cfoRecommendations = JSON.parse(fields.CFORecommendations || "[]"); } catch {}

  return {
    id: fields.id || fields.ID || fields._spItemId,
    title: fields.Title || "",
    description: fields.Description || "",
    category: fields.Category || "General",
    priority: fields.Priority || "Medium",
    status: fields.Status || "Not Started",
    dueDate: fields.DueDate || "",
    userDept: fields.UserDept || "",
    entity: fields.Entity || "",
    contractor: fields.Contractor || "",
    lookAhead: fields.LookAhead || "",
    owners,
    ownerEmail:      owners[0]?.email      || "",
    ownerName:       owners[0]?.name       || "",
    ownerJobTitle:   owners[0]?.jobTitle   || "",
    ownerDepartment: owners[0]?.department || "",
    assignedTo: fields.AssignedTo || "",
    assignedToName: fields.AssignedToName || "",
    createdBy: fields.CreatedBy || "",
    createdByName: fields.CreatedByName || "",
    createdAt: fields.CreatedAt || new Date().toISOString(),
    updatedAt: fields.UpdatedAt || new Date().toISOString(),
    updates,
    cfoIssue: fields.CFOIssue === "true",
    cfoRecommendations,
    _spItemId: fields.id || fields.ID,
  };
}

function activityToSp(activity) {
  // Serialize owners array to pipe-separated fields; fall back to legacy single-owner fields
  const owners = activity.owners?.length > 0
    ? activity.owners
    : (activity.ownerEmail ? [{ email: activity.ownerEmail, name: activity.ownerName || "", jobTitle: activity.ownerJobTitle || "", department: activity.ownerDepartment || "" }] : []);

  return {
    Title: activity.title,
    Description: activity.description || "",
    Category: activity.category,
    Priority: activity.priority,
    Status: activity.status,
    DueDate: activity.dueDate || null,
    UserDept: activity.userDept || "",
    Entity: activity.entity || "",
    Contractor: activity.contractor || "",
    LookAhead: activity.lookAhead || "",
    OwnerEmail:      owners.map(o => o.email).join("|"),
    OwnerName:       owners.map(o => o.name).join("|"),
    OwnerJobTitle:   owners[0]?.jobTitle   || "",
    OwnerDepartment: owners[0]?.department || "",
    AssignedTo: activity.assignedTo || "",
    AssignedToName: activity.assignedToName || "",
    CreatedBy: activity.createdBy || "",
    CreatedByName: activity.createdByName || "",
    CreatedAt: activity.createdAt || new Date().toISOString(),
    UpdatedAt: new Date().toISOString(),
    UpdatesData: JSON.stringify(activity.updates || []),
    CFOIssue: activity.cfoIssue ? "true" : "",
    CFORecommendations: JSON.stringify(activity.cfoRecommendations || []),
  };
}

export const CLOSED_STATUS = "Closed";

/**
 * The status a task actually ended on.
 *
 * "Closed" is stored in the same field as the workflow status, so closing a
 * task overwrites whatever it was — a completed task and an abandoned one look
 * identical afterwards. The progress trail still holds the answer: each update
 * records the status at the time it was written, newest first, so the most
 * recent non-Closed entry is the state the task was closed from.
 *
 * Returns the live status for anything still open.
 */
export function finalStatusOf(activity) {
  if (!activity) return "";
  if (activity.status !== CLOSED_STATUS) return activity.status;
  const prior = (activity.updates || []).find(
    (u) => u.status && u.status !== CLOSED_STATUS,
  );
  return prior?.status || CLOSED_STATUS;
}

/** True when the task was completed before it was closed. */
export function wasCompletedAtClose(activity) {
  return activity?.status === CLOSED_STATUS && finalStatusOf(activity) === "Completed";
}

/** Completed work, whether the task is still open or has since been closed. */
export function isCompletedWork(activity) {
  return finalStatusOf(activity) === "Completed";
}

/**
 * The latest note describing actual work, for the Key Activities column and
 * the reports.
 *
 * Closing, reopening and retrospective corrections all write to the same trail
 * so the audit history stays complete, but those entries describe bookkeeping
 * rather than progress. Left unfiltered they would displace the real update in
 * every report the moment a task was reopened.
 */
export function latestProgressNote(activity) {
  const entry = (activity?.updates || []).find((u) => !u.system && u.note);
  return entry?.note || "";
}

/** True when a closed task has no record of the status it ended on. */
export function hasUnrecordedOutcome(activity) {
  return activity?.status === CLOSED_STATUS && finalStatusOf(activity) === CLOSED_STATUS;
}

/**
 * Build the trail entry that records a closed task's outcome after the fact.
 * Flagged as a correction so the history shows plainly that the outcome was
 * entered later rather than captured at the time.
 */
export function outcomeBackfillEntry(status, byEmail, byName) {
  return {
    id: `u-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    date: new Date().toISOString(),
    by: byEmail,
    byName,
    status,
    note: `Outcome recorded retrospectively: this task was "${status}" when it was closed.`,
    lookAhead: "",
    system: true,
    backfilled: true,
  };
}

export async function fetchActivities(token) {
  const siteId = await getSiteId(token);
  // No $orderby — custom columns are not indexed by default in SharePoint
  // and Graph API rejects $orderby on unindexed columns. Sort in memory instead.
  const raw = await fetchAllPages(
    token,
    `${graphBase}/sites/${siteId}/lists/${ACTIVITIES_LIST}/items?expand=fields&$top=${GRAPH_PAGE_SIZE}`,
    { errorMessage: "Failed to fetch activities" },
  );
  const items = raw.map((i) => spToActivity({ ...i.fields, id: i.id }));
  // Sort newest-updated first
  return items.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
}

export async function createActivity(token, activity) {
  const siteId = await getSiteId(token);
  const res = await fetch(
    `${graphBase}/sites/${siteId}/lists/${ACTIVITIES_LIST}/items`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ fields: activityToSp(activity) }),
    },
  );
  if (!res.ok) {
    const e = await res.json();
    throw new Error(e.error?.message || "Failed to create activity");
  }
  const item = await res.json();
  return spToActivity({ ...item.fields, id: item.id });
}

export async function updateActivity(token, spItemId, activity) {
  const siteId = await getSiteId(token);
  const res = await fetch(
    `${graphBase}/sites/${siteId}/lists/${ACTIVITIES_LIST}/items/${spItemId}`,
    {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ fields: activityToSp(activity) }),
    },
  );
  if (!res.ok) throw new Error("Failed to update activity");
}

export async function deleteActivity(token, spItemId) {
  const siteId = await getSiteId(token);
  const res = await fetch(
    `${graphBase}/sites/${siteId}/lists/${ACTIVITIES_LIST}/items/${spItemId}`,
    { method: "DELETE", headers: { Authorization: `Bearer ${token}` } },
  );
  if (!res.ok) throw new Error("Failed to delete activity");
}
